import { useRef, forwardRef, useImperativeHandle } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/atoms/button';

export interface SignaturePadHandle {
  getDataURL: () => string | null;
  isEmpty: () => boolean;
  clear: () => void;
}

interface SignaturePadProps {
  disabled?: boolean;
  onClear?: () => void;
  /** Hide the built-in Clear button so the caller can group it with its own
   *  actions (e.g. Clear + Save side by side) via the exposed `clear()` handle. */
  hideClearButton?: boolean;
  /** Render ONLY the drawing canvas — no border box, no wrapper spacing, no
   *  Clear button — so the caller can frame it (e.g. with a hint line + footer).
   *  Height is controlled by `height`. */
  unstyled?: boolean;
  /** Canvas height in px (default 160). */
  height?: number;
  /** Fired on the first stroke of a signing gesture — lets the caller hide a
   *  "Draw your signature here" hint. */
  onBegin?: () => void;
}

const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  (
    { disabled, onClear, hideClearButton = false, unstyled = false, height = 160, onBegin },
    ref,
  ) => {
    const canvasRef = useRef<ReactSignatureCanvas>(null);

    useImperativeHandle(ref, () => ({
      getDataURL: () => {
        if (!canvasRef.current || canvasRef.current.isEmpty()) return null;
        return canvasRef.current.toDataURL('image/png');
      },
      isEmpty: () => canvasRef.current?.isEmpty() ?? true,
      clear: () => canvasRef.current?.clear(),
    }));

    const canvas = (
      <ReactSignatureCanvas
        ref={canvasRef}
        penColor="#111827"
        onBegin={onBegin}
        canvasProps={{
          style: { width: '100%', height, display: 'block' },
        }}
      />
    );

    if (unstyled) return canvas;

    return (
      <div className="space-y-2">
        <div className="touch-none rounded-lg border-2 border-dashed border-gray-300 bg-white">
          {canvas}
        </div>
        {!disabled && !hideClearButton && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              canvasRef.current?.clear();
              onClear?.();
            }}
          >
            Clear
          </Button>
        )}
      </div>
    );
  },
);

SignaturePad.displayName = 'SignaturePad';
export default SignaturePad;
